import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Loader2, Maximize2, X } from "lucide-react";
import { useState } from "react";
import { useToast } from "../../components/ui/toast";
import { CORE, coreText, downloadBlob } from "../../lib/api";

interface Props {
  /** Core path of the rendered document, ending `/report.html`. The PDF is the same path ending `.pdf`. */
  path: string;
  title: string;
  filename: string;
}

/**
 * A report rendered by Intelligence, shown the way Claude shows an HTML
 * artifact: the document as-is, in a sandboxed frame.
 *
 * `sandbox=""` grants nothing -- no script, no same-origin, no forms, no
 * navigation of this page. The document needs none of it: its charts are
 * inline SVG and its CSS is inline. `srcDoc` rather than `src` because the
 * route needs the bearer token, which an iframe cannot send.
 */
export function ReportFrame({ path, title, filename }: Props) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  // An answer never changes once written, so neither does its rendering.
  const html = useQuery({ queryKey: ["report-html", path], queryFn: () => coreText(path), staleTime: Infinity });

  const saveHtml = () => {
    if (!html.data) return;
    const url = URL.createObjectURL(new Blob([html.data], { type: "text/html;charset=utf-8" }));
    const link = document.createElement("a"); link.href = url; link.download = `${filename}.html`; link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const savePdf = () => downloadBlob(`${CORE}${path.replace(/\.html$/, ".pdf")}`, `${filename}.pdf`)
    .catch(e => toast.warning("Could not download PDF", (e as Error).message));

  const toolbar = <div className="flex items-center gap-1 border-b border-line px-3 py-2">
    <FileText size={14} className="text-brand" />
    <span className="mr-auto truncate text-xs font-medium text-ink-2">{title}</span>
    <button onClick={saveHtml} disabled={!html.data} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-2 hover:bg-soft disabled:opacity-40"><Download size={13} /> HTML</button>
    <button onClick={savePdf} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-2 hover:bg-soft"><Download size={13} /> PDF</button>
    <button onClick={() => setExpanded(!expanded)} aria-label={expanded ? "Close report" : "Expand report"} className="rounded-md p-1.5 text-ink-2 hover:bg-soft">{expanded ? <X size={14} /> : <Maximize2 size={14} />}</button>
  </div>;

  const body = html.isLoading
    ? <p className="flex items-center gap-2 p-4 text-sm text-ink-3"><Loader2 size={16} className="animate-spin" /> Rendering report…</p>
    : html.isError
      ? <div role="alert" className="p-4 text-sm text-danger">{(html.error as Error).message}<button onClick={() => html.refetch()} className="ml-3 underline">Retry</button></div>
      : <iframe title={title} sandbox="" srcDoc={html.data} className="block w-full flex-1 border-0 bg-white" style={expanded ? undefined : { height: 520 }} />;

  if (expanded) {
    return <div role="dialog" aria-label={title} className="fixed inset-0 z-50 flex flex-col bg-elevated">{toolbar}{body}</div>;
  }
  return <section className="overflow-hidden rounded-xl border border-line bg-elevated">{toolbar}{body}</section>;
}
